import React from 'react';
import { observer } from 'mobx-react-lite';
import { useAppStateContext } from 'src/AppState/AppState';
import { ChannelType } from 'src_server/trpc/fraudAndCompliance';
import { MenuItemsNavigationFraud } from 'src/components/FraudAndCompliance/FraudAndComplianceTabItems.style';
import { ArrowIconWrapper, MenuButtonWrapper, MenuCounter } from 'src/components/Menu/Menu.style';
import { useCommon } from 'src/AppState/Common';
import { ConfigState } from 'src/AppState/ConfigState/ConfigState';
type FraudAndComplianceRouteType =
    | ChannelType
    | 'manual-withdrawals'
    | 'automatic-withdrawals'
    | 'gamstop-account-not-longer-blocked'
    | 'moh-popup-triggers'
    | 'self-exclusion-changes';
export const FraudAndComplianceTabItems = observer((): JSX.Element | null => {
    const common = useCommon();
    const { routerBackoffice, fraudAndComplianceState } = useAppStateContext();
    const {
        config: { isAffordability, isPEP, withdrawalsTabsShow, idMission },
    } = ConfigState.get(common);
    const customerParams = routerBackoffice.getParametersString('global', 'tab') ?? 'manual-withdrawals';

    const isTabActive = (label: string): boolean => {
        return customerParams === label;
    };

    interface MenuItemsType {
        id: FraudAndComplianceRouteType;
        label: string;
        count: number;
        isItemVisible: boolean;
        onClick: () => void;
    }

    const menuItems: Array<MenuItemsType> = [
        {
            id: 'manual-withdrawals',
            label: 'Manual Withdrawals',
            count: fraudAndComplianceState.manualWithdrawalsNotificationsCount,
            isItemVisible: withdrawalsTabsShow,
            onClick: (): void => routerBackoffice.redirectToManualWithdrawals(),
        },
        {
            id: 'automatic-withdrawals',
            label: 'Automatic Withdrawals',
            count: fraudAndComplianceState.automaticWithdrawalsNotificationsCount,
            isItemVisible: withdrawalsTabsShow,
            onClick: (): void => routerBackoffice.redirectToAutomaticWithdrawals(),
        },
        {
            id: 'affordability-excluded',
            label: 'Affordability Check',
            count: fraudAndComplianceState.findCount('affordability-excluded'),
            isItemVisible: isAffordability,
            onClick: (): void => routerBackoffice.redirectToAffordabilityExcluded(),
        },
        {
            id: 'call-credit-pep-verification',
            label: 'PEP & Sanctions',
            count: fraudAndComplianceState.findCount('call-credit-pep-verification'),
            isItemVisible: isPEP,
            onClick: (): void => routerBackoffice.redirectToPEP(),
        },
        {
            id: 'matched-account',
            label: 'Matched Accounts',
            count: fraudAndComplianceState.findCount('matched-account'),
            isItemVisible: true,
            onClick: (): void => routerBackoffice.redirectToMatchedAccount(),
        },
        {
            id: 'payment-declines',
            label: 'Payment Declines',
            count: fraudAndComplianceState.findCount('payment-declines'),
            isItemVisible: true,
            onClick: (): void => routerBackoffice.redirectToPaymentDeclines(),
        },
        {
            id: 'new-matched-payment-methods',
            label: 'Matched Payment Methods',
            count: fraudAndComplianceState.findCount('new-matched-payment-methods'),
            isItemVisible: true,
            onClick: (): void => routerBackoffice.redirectToMatchedPaymentMethods(),
        },
        {
            id: 'review-account',
            label: 'Key Account Changes',
            count: fraudAndComplianceState.findCount('review-account'),
            isItemVisible: true,
            onClick: (): void => routerBackoffice.redirectToReviewAccount(),
        },
        {
            id: 'call-credit-age-verification',
            label: 'DOB Mismatch',
            count: fraudAndComplianceState.findCount('call-credit-age-verification'),
            isItemVisible: true,
            onClick: (): void => routerBackoffice.redirectToCallCreditAgeVerification(),
        },
        {
            id: 'call-credit-validation-errors',
            label: 'Manual AV',
            count: fraudAndComplianceState.findCount('call-credit-validation-errors'),
            isItemVisible: true,
            onClick: (): void => routerBackoffice.redirectToCallCreditValidationErrors(),
        },
        {
            id: 'gamstop-account-not-longer-blocked',
            label: 'GamStop',
            count: fraudAndComplianceState.findCount('gamstop-changes'),
            isItemVisible: true,
            onClick: (): void => routerBackoffice.redirectToGamstopAccountNotLongerBlocked(),
        },
        {
            id: 'self-exclusion-changes',
            label: 'Self Excluded',
            count: fraudAndComplianceState.findCount('self-exclusion-changes'),
            isItemVisible: true,
            onClick: (): void => routerBackoffice.redirectToSelfExclusionChanges(),
        },
        {
            id: 'deposit-restrictions',
            label: 'Deposit Restrictions',
            count: fraudAndComplianceState.findCount('deposit-restrictions'),
            isItemVisible: true,
            onClick: (): void => routerBackoffice.redirectToReviewDepositRestrictions(),
        },
        {
            id: 'id-mission-verifications',
            label: 'EKYC',
            count: fraudAndComplianceState.findCount('id-mission-verifications'),
            isItemVisible: idMission,
            onClick: (): void => routerBackoffice.redirectToIdMissionVerifications(),
        },
        {
            id: 'moh-popup-triggers',
            label: 'MoH Notifications',
            count: fraudAndComplianceState.findCount('moh-popup-triggers'),
            isItemVisible: true,
            onClick: (): void => routerBackoffice.redirectToMohPopupTriggers(),
        },
    ];

    return (
        <MenuItemsNavigationFraud>
            {menuItems.map((linkItem) => {
                const count = linkItem.count;
                const countForView = count > 99 ? '99+' : count;

                if (linkItem.isItemVisible) {
                    return (
                        <MenuButtonWrapper
                            data-test={linkItem.id}
                            hasCounter={count > 0}
                            isActive={isTabActive(linkItem.id)}
                            key={linkItem.id}
                            onClick={linkItem.onClick}
                        >
                            {linkItem.label}
                            {count > 0 && (
                                <MenuCounter
                                    isActive={isTabActive(linkItem.id)}
                                    data-test={`${linkItem.id}-menu-counter`}
                                >
                                    {countForView}
                                </MenuCounter>
                            )}
                            <ArrowIconWrapper
                                isActive={isTabActive(linkItem.id)}
                                position='right'
                            />
                        </MenuButtonWrapper>
                    );
                }

                return null;
            })}
        </MenuItemsNavigationFraud>
    );
});
